import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import PageMeta from "../components/PageMeta";
import CTABand from "../components/CTABand";
import { POSTS } from "../data/content";

const Resources = () => {
  const [active, setActive] = useState("All");

  const categories = useMemo(() => ["All", ...new Set(POSTS.map((p) => p.category))], []);
  const filtered = active === "All" ? POSTS : POSTS.filter((p) => p.category === active);

  return (
    <main data-testid="resources-page">
      <PageMeta title="Resource Centre | AccuSurge Medical Solutions" description="Guides on hospital setup, dialysis centre setup, hospital equipment procurement, preventive maintenance and healthcare infrastructure in India." />
      <section className="bg-brand-950 pb-20 pt-40 md:pb-28 grain relative overflow-hidden">
        <div className="container-x relative">
          <Reveal>
            <span className="eyebrow text-accent">Resource Centre</span>
            <h1 className="mt-6 max-w-4xl font-heading text-4xl md:text-6xl font-semibold tracking-tighter text-white">
              Healthcare Knowledge, Practically Written.
            </h1>
            <p className="mt-6 max-w-2xl text-base md:text-lg text-white/70">
              Guides for healthcare entrepreneurs and administrators — hospital setup, equipment planning, procurement
              and maintenance.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="sticky top-[76px] z-30 border-b border-slate-200 bg-white/90 py-4 backdrop-blur-xl">
        <div className="container-x flex gap-2 overflow-x-auto [scrollbar-width:none]">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActive(c)}
              className={`shrink-0 rounded-full px-4 py-2 text-[13px] font-semibold transition-colors ${
                active === c ? "bg-brand-600 text-white" : "border border-slate-200 bg-white text-slate-600 hover:border-brand-300"
              }`}
              data-testid={`resource-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container-x">
          <Stagger className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p) => (
              <StaggerItem key={p.id}>
                <Link
                  to={`/resources/${p.slug}`}
                  className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.07)]"
                  data-testid={`article-card-${p.slug}`}
                >
                  <div className="h-48 overflow-hidden">
                    <img src={p.image} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                  </div>
                  <div className="flex flex-1 flex-col p-7">
                    <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-accent-600">{p.category}</span>
                    <h2 className="mt-2 font-heading text-lg font-semibold leading-snug text-ink">{p.title}</h2>
                    <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-slate-600">{p.excerpt}</p>
                    <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-brand-700 transition-colors group-hover:text-accent-600">
                      Read article <ArrowRight className="h-4 w-4" />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <CTABand />
    </main>
  );
};

export default Resources;
